import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import {
  NgbDropdownModule,
  NgbModalModule,
  NgbNavModule,
  NgbPopoverModule,
  NgbTooltipModule,
  NgbTypeaheadModule
} from '@ng-bootstrap/ng-bootstrap';
import { NgSelectModule } from '@ng-select/ng-select';
import { NgxDatatableModule } from '@swimlane/ngx-datatable';
import { AgGridModule } from 'ag-grid-angular';
import { AngularDualListBoxModule } from 'angular-dual-listbox';
import { NgxMaskDirective, NgxMaskPipe, provideNgxMask } from 'ngx-mask';
import { DatetimePickerModule } from 'src/app/core/components/datetime-picker/datetime-picker.module';
import { ExpandableTextModule } from 'src/app/core/components/expandable-text/expandable-text.module';
import { MemberDetailsComponent } from '../private/member-details/member-details.component';
import { AddressModule } from './components/address/address.module';
import { CustomDatatableFooterModule } from './components/custom-datatable-footer/custom-datatable-footer.module';
import { CompactDateGridRendererComponent } from './components/grid/compact-date-grid-renderer/compact-date-grid-renderer.component';
import { DataGridComponent } from './components/grid/data-grid/data-grid.component';
import { GridButtonCellRendererComponent } from './components/grid/grid-buttton-cell-renderer/grid-button-cell-renderer.component';
import { GridFooterComponent } from './components/grid/grid-footer/grid-footer.component';
import { IconCellRendererComponent } from './components/grid/icon-cell-renderer/icon-cell-renderer.component';
import { LinkCellRendererComponent } from './components/grid/link-cell-renderer/link-cell-renderer.component';
import { CustomLoadingOverlayComponent } from './components/grid/overlays/custom-loading-overlay.component';
import { CustomNoRowsOverlayComponent } from './components/grid/overlays/custom-no-rows-overlay.component';
import { PatientPhoneCellRendererComponent } from './components/grid/patient-phone-cell-renderer/patient-phone-cell-renderer.component';
import { NavWrappedModule } from './components/nav-wrapped/nav-wrapped.module';
import { SignaturePadModule } from './components/signature-pad/signature-pad.module';
import { AutoFocusDirective } from './directives/auto-focus.directive';
import { BackButtonDirective } from './directives/back-button.directive';
import { BootstrapCustomFileInputDirective } from './directives/bootstrap-custom-file-input.directive';
import { BootstrapFormInputsDirective } from './directives/bootstrap-form-inputs.directive';
import { BootstrapFormDirective } from './directives/bootstrap-form.directive';
import { CareTeamRoleValidatorDirective } from './directives/care-team-role-validator.directive';
import { ConfirmEqualValidatorDirective } from './directives/confirm-equal-validator.directive';
import { CustomRequiredValidatorDirective } from './directives/custom-required-validator.directive';
import { GlobalModalDirective } from './directives/global-modal.directive';
import { InvalidCharacterRemoverDirective } from './directives/invalid-character-remover.directive';
import { LoadingIndicatorDirective } from './directives/loading-indicator.directive';
import { MaxFileSizeValidatorDirective } from './directives/max-file-size-validator.directive';
import { NgbTypeaheadExtendedDirective } from './directives/ngb-typeahead-extended.directive';
import { NotEmptyValidatorDirective } from './directives/not-empty-validator.directive';
import { NumericValidatorDirective } from './directives/numeric-validator.directive';
import { UniqueMemberIdValidatorDirective } from './directives/unique-memberid-validator.directive';
import { UniqueMRNValidatorDirective } from './directives/unique-mrn-validator.directive';
import { UniqueRINValidatorDirective } from './directives/unique-rin-validator.directive';
import { UniqueUsernameValidatorDirective } from './directives/unique-username-validator.directive';
import { ValidateUserPasswordDirective } from './directives/validate-user-password.directive';
import { PipesModule } from './pipes/pipes.module';

@NgModule({
  providers: [provideNgxMask({ validation: true })],
  declarations: [
    GlobalModalDirective,
    ConfirmEqualValidatorDirective,
    MaxFileSizeValidatorDirective,
    ValidateUserPasswordDirective,
    BootstrapFormDirective,
    BootstrapCustomFileInputDirective,
    BootstrapFormInputsDirective,
    NotEmptyValidatorDirective,
    UniqueMemberIdValidatorDirective,
    NumericValidatorDirective,
    UniqueUsernameValidatorDirective,
    AutoFocusDirective,
    LoadingIndicatorDirective,
    CustomRequiredValidatorDirective,
    CareTeamRoleValidatorDirective,
    NgbTypeaheadExtendedDirective,
    UniqueMRNValidatorDirective,
    UniqueRINValidatorDirective,
    InvalidCharacterRemoverDirective,
    BackButtonDirective,
    MemberDetailsComponent,
    GridButtonCellRendererComponent,
    LinkCellRendererComponent,
    CustomNoRowsOverlayComponent,
    CustomLoadingOverlayComponent,
    PatientPhoneCellRendererComponent,
    CompactDateGridRendererComponent,
    DataGridComponent,
    GridFooterComponent,
    IconCellRendererComponent
  ],
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    NgbDropdownModule,
    NgbModalModule,
    NgbNavModule,
    NgbTypeaheadModule,
    NgbPopoverModule,
    NgbTooltipModule,
    // TagInputModule,
    PipesModule,
    DatetimePickerModule,
    ExpandableTextModule,
    CustomDatatableFooterModule,
    // Should probably be broken up and imported into other modules
    SignaturePadModule,
    AddressModule,
    NavWrappedModule,
    NgxDatatableModule,
    AngularDualListBoxModule,
    RouterModule,
    AgGridModule,
    NgxMaskDirective,
    NgxMaskPipe,
    NgSelectModule
  ],
  exports: [
    GlobalModalDirective,
    ConfirmEqualValidatorDirective,
    MaxFileSizeValidatorDirective,
    ValidateUserPasswordDirective,
    BootstrapFormDirective,
    BootstrapCustomFileInputDirective,
    BootstrapFormInputsDirective,
    NotEmptyValidatorDirective,
    UniqueMemberIdValidatorDirective,
    NumericValidatorDirective,
    UniqueUsernameValidatorDirective,
    AutoFocusDirective,
    LoadingIndicatorDirective,
    CustomRequiredValidatorDirective,
    CareTeamRoleValidatorDirective,
    NgbTypeaheadExtendedDirective,
    UniqueMRNValidatorDirective,
    UniqueRINValidatorDirective,
    InvalidCharacterRemoverDirective,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    NgbDropdownModule,
    NgbModalModule,
    NgbNavModule,
    NgbTypeaheadModule,
    NgbPopoverModule,
    NgbTooltipModule,
    // TagInputModule,
    PipesModule,
    DatetimePickerModule,
    ExpandableTextModule,
    CustomDatatableFooterModule,
    // See comment above in imports
    AgGridModule,
    SignaturePadModule,
    AddressModule,
    NavWrappedModule,
    NgxDatatableModule,
    AngularDualListBoxModule,
    BackButtonDirective,
    MemberDetailsComponent,
    DataGridComponent,
    NgxMaskDirective,
    NgxMaskPipe,
    NgSelectModule,
    GridFooterComponent
  ]
})
export class AppCoreModule {}
